import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { track } from "@/lib/track";

function b64ToUint8(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function NotificationSettings() {
  const [s, setS] = useState(null);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const perm = supported ? Notification.permission : "unsupported";

  const load = async () => {
    api.get("/push/settings").then((r) => setS(r.data)).catch(() => {});
    if (supported) { try { const reg = await navigator.serviceWorker.ready; setSub(await reg.pushManager.getSubscription()); } catch (e) { console.warn("push subscription lookup failed", e?.message); } }
  };
  useEffect(() => { load(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const p = await Notification.requestPermission();
      if (p !== "granted") { toast.error("Izin notifikasi ditolak. Aktifkan lewat pengaturan browser."); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) });
      await api.post("/push/subscribe", subscription.toJSON());
      track("push_enable");
      toast.success("Notifikasi diaktifkan di perangkat ini");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { if (sub) { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); await sub.unsubscribe(); } track("push_disable"); toast.success("Notifikasi dimatikan di perangkat ini"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async (patch) => { try { const { data } = await api.put("/push/settings", patch); setS(data); toast.success("Setting disimpan"); } catch (e) { toast.error(errMsg(e)); } };
  const test = async () => { try { await api.post("/push/test"); toast.success("Notifikasi tes dikirim"); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="soft-card p-6" data-testid="notification-settings">
      <div className="flex items-center gap-2 text-sm font-medium"><Bell className="h-4 w-4 text-secondary" />Notifikasi</div>
      <p className="mt-1 text-xs text-muted-foreground">Pengingat belajar harian dari maskot Yutaka, pengumuman lembaga, dan ucapan selamat saat lulus evaluasi atau streak 7 hari.</p>
      {!supported && <p data-testid="notification-unsupported" className="mt-4 rounded-lg bg-muted px-3 py-2 text-xs">{isIOS && !standalone ? "iPhone/iPad: pasang dulu aplikasi ke Layar Utama (Safari → Bagikan → Tambah ke Layar Utama, iOS 16.4+), lalu buka dari ikon Yutaka untuk mengaktifkan notifikasi." : "Browser ini belum mendukung notifikasi push."}</p>}
      {supported && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {sub ? (
            <>
              <span data-testid="notification-status" className="inline-flex items-center gap-1.5 rounded-full bg-sage-soft px-3 py-1.5 text-xs font-semibold text-sage"><BellRing className="h-3.5 w-3.5" />Aktif di perangkat ini</span>
              <button data-testid="notification-test-button" onClick={test} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent"><Send className="h-3.5 w-3.5" />Kirim tes</button>
              <button data-testid="notification-disable-button" disabled={busy} onClick={disable} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-accent"><BellOff className="h-3.5 w-3.5" />Matikan</button>
            </>
          ) : (
            <button data-testid="notification-enable-button" disabled={busy || perm === "denied"} onClick={enable} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"><Bell className="h-4 w-4" />{perm === "denied" ? "Izin ditolak di browser" : "Aktifkan notifikasi"}</button>
          )}
          {isIOS && !standalone && <p className="w-full text-xs text-muted-foreground">iPhone/iPad: notifikasi hanya bekerja bila aplikasi dibuka dari ikon di Layar Utama.</p>}
        </div>
      )}
      {s && (
        <div className="mt-5 grid gap-4 border-t border-border pt-5 sm:grid-cols-2">
          <label className="flex items-center justify-between gap-3 text-sm" data-testid="reminder-toggle-row"><span>Pengingat harian (jika belum belajar)</span>
            <button role="switch" aria-checked={s.reminder_enabled} data-testid="reminder-toggle" onClick={() => save({ reminder_enabled: !s.reminder_enabled })} className={`relative h-6 w-11 rounded-full transition-colors ${s.reminder_enabled ? "bg-sage" : "bg-muted"}`}><span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${s.reminder_enabled ? "translate-x-5" : "translate-x-0.5"}`} /></button>
          </label>
          <label className="flex items-center justify-between gap-3 text-sm"><span>Jam pengingat (waktu Jepang / JST)</span>
            <input type="time" data-testid="reminder-time-input" value={s.reminder_time} onChange={(e) => setS({ ...s, reminder_time: e.target.value })} onBlur={(e) => save({ reminder_time: e.target.value })} className="rounded-lg border border-border bg-card px-2 py-1 text-sm" />
          </label>
          <p className="text-xs text-muted-foreground sm:col-span-2">Perangkat terdaftar: {s.subscriptions}. Pengingat dikirim pada jam di atas hanya bila hari itu kamu belum menyelesaikan satu aktivitas pun.</p>
        </div>
      )}
    </div>
  );
}
